import React, { useState, useEffect, useMemo } from "react";
import { Grid, TextField, Box, Typography } from "@mui/material";
import { ViewTicketProps } from "./types";
import { getTicketsList } from "../../api/afp-support-tickets";
import { useSetLoading } from "../../common/hooks/appearance";
import { AlertComponent } from "../../common/components";
import { TextInputComponent } from "../../common/components";
import TicketAttachments from "./ticket-attachments";
import { DescriptionInputComponent } from "../../common/components";

const ViewTicket: React.FC<ViewTicketProps> = ({ id }) => {
  const [ticketData, setTicketData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const fetchTicketData = async (ticketId: number) => {
    try {
      setLoading(true);
      const response = await getTicketsList({}, ticketId);
      if (response?.data?.data) {
        setTicketData(response?.data?.data?.tickets[0]);
      }
    } catch (error: any) {
      console.error("Error fetching ticket data:", error);
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch ticket data."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchTicketData(id);
  }, [id]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="view-ticket">
      <Box sx={{ m: 3, p: 2 }} className="view-ticket-wrapper">
        {ticketData?.id && (
          <div className="details-wrapper">
            <div className="info-wrapper">
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "600 !important" }}
              >
                Ticket Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    type="text"
                    fieldName="raised_by"
                    label="Name"
                    value={ticketData?.raised_by || ""}
                    readOnly={true}
                    renderErrorMessage={false}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    type="text"
                    fieldName="email"
                    label="Email"
                    value={ticketData?.email || ""}
                    readOnly={true}
                    renderErrorMessage={false}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    type="text"
                    fieldName="phone_number"
                    label="Phone Number"
                    value={ticketData?.phone || ""}
                    readOnly={true}
                    renderErrorMessage={false}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    type="text"
                    fieldName="subject"
                    label="Subject"
                    value={ticketData?.subject || ""}
                    readOnly={true}
                    renderErrorMessage={false}
                  />
                </Grid>

                {/* <Grid item xs={12} sm={6} md={4}>
                <TextInputComponent
                  type="text"
                  fieldName="partner_name"
                  label="Partner Name"
                  value={ticketData?.tenant?.company_name || ""}
                  readOnly={true}
                  renderErrorMessage={false}
                />
              </Grid> */}

                <Grid item xs={12} sm={6} md={4}>
                  <label className="description-label">Description</label>
                  <DescriptionInputComponent
                    fieldName="description"
                    label="Description"
                    value={ticketData?.description || ""}
                    rows={4}
                    readOnly={true}
                    renderErrorMessage={false}
                  />
                </Grid>

                {ticketData?.status === "Closed" && (
                  <Grid item xs={12} sm={6} md={4}>
                    <label className="description-label">Notes</label>
                    <DescriptionInputComponent
                      fieldName="notes"
                      label="Notes"
                      value={ticketData?.comments || ""}
                      rows={4}
                      readOnly={true}
                      renderErrorMessage={false}
                    />
                  </Grid>
                )}
              </Grid>
            </div>
            {ticketData?.attachments?.length > 0 && (
              <div className="attachments-wrapper">
                <p className="attachment-title">Attachments</p>
                <TicketAttachments attachmentList={ticketData?.attachments} />
              </div>
            )}
          </div>
        )}
      </Box>

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ViewTicket;
